import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import BottomSheet from '../../DesignSystem/BottomSheet';
import Button from '../../DesignSystem/Button';
import { saveNewAddress, editAddress } from '../../../../../services';

import s from './AddNewAddress.module.scss';

import { ReactComponent as HomeIcon } from '../../Icons/Home.svg';
import { ReactComponent as WorkIcon } from '../../Icons/Work.svg';
import { ReactComponent as OtherIcon } from '../../Icons/Location-Outlined.svg';
import { ReactComponent as CheckIcon } from '../../Icons/Check.svg';
import useSessionStorage from '../../hooks/useStorage';
const ADDRESS_TYPES = [
  { key: 'Home', Icon: HomeIcon },
  { key: 'Work', Icon: WorkIcon },
  { key: 'Other', Icon: OtherIcon },
];

const AddNewAddress = ({
  isOpen,
  onClose,
  newAddress,
  onSave,
  editingAddress,
  hideSkipButton = false,
  onSkipClick,
}) => {
  const authToken = useSelector((state) => state?.user?.authToken);
  const [addressType, setAddressType] = useState('Home');
  const [addressLine1, setAddressLine1] = useState('');
  const [locality, setLocality] = useState('');
  const [pincode, setPincode] = useState('');
  const [saving, setSaving] = useState(false);

  const { setToStorage, getFromStorage } = useSessionStorage();

  /* ------------------------------
     Reset form when modal closes
  ------------------------------ */
  useEffect(() => {
    if (!isOpen) {
      // Clear all fields when modal closes
      setAddressType('Home');
      setAddressLine1('');
      setLocality('');
      setPincode('');
      setSaving(false);
    }
  }, [isOpen]);

  /* ------------------------------
     Prefill from map or edit mode when modal opens
  ------------------------------ */
  useEffect(() => {
    if (isOpen) {
      if (editingAddress) {
        // Editing existing address - pre-fill all fields
        // if not work or home then store as other
        setAddressType(
          editingAddress?.title?.toLowerCase() === 'work' ||
            editingAddress?.title?.toLowerCase() === 'home'
            ? editingAddress?.title?.charAt(0).toUpperCase() +
                editingAddress?.title?.slice(1)
            : 'Other'
        );
        setAddressLine1(editingAddress?.address || '');
        setLocality(editingAddress?.locality || '');
        setPincode(
          editingAddress?.pincode !== undefined &&
            editingAddress?.pincode !== null
            ? String(editingAddress.pincode)
            : ''
        );
      } else if (newAddress) {
        // New address from map - pre-fill what we have
        setAddressLine1(newAddress?.fullAddress || newAddress?.address || '');
        setLocality(newAddress?.locality || '');
        setPincode(newAddress?.pincode || '');
      }
    }
  }, [isOpen, editingAddress, newAddress]);

  /* ------------------------------
     Validation
  ------------------------------ */
  const isValid =
    addressLine1.trim().length > 5 &&
    locality.trim().length > 2 &&
    String(pincode).length === 6;

  console.log('is valid', isValid);

  /* ------------------------------
     Save Handler
  ------------------------------ */
  const handleSave = async () => {
    if (isValid === false || saving) return;
    setSaving(true);
    console.log('newAddress', newAddress, 'editingAddress', editingAddress);
    const payload = {
      title: addressType,
      address: addressLine1,
      locality,
      pincode: Number(pincode),
      lat: editingAddress?.lat || newAddress?.lat,
      long: editingAddress?.long || newAddress?.long,
    };
    try {
      let res;
      if (editingAddress?.addressId) {
        // Edit existing address
        res = await editAddress(
          { ...payload, addressId: editingAddress.addressId },
          authToken
        );
      } else {
        // Create new address
        res = await saveNewAddress(payload, authToken);
      }

      // Success callback to refresh list
      onSave?.(res);

      // Close modal (reset will happen via useEffect)
      onClose();
    } catch (error) {
      console.error('Error saving address:', error);
    } finally {
      setSaving(false);
    }
  };

  // save temp address

  function saveTempAddress() {
    if (newAddress) {
      let lastAddress = getFromStorage('selected_address');
      // Even if location is same, we should still allow closing the modal
      // and proceeding. The early return was preventing the button from working.
      setToStorage('selected_address', {
        address: addressLine1,
        locality,
        pincode: Number(pincode),
        lat: editingAddress?.lat || newAddress?.lat,
        long: editingAddress?.long || newAddress?.long,
      });
      // dispatch event
      window.dispatchEvent(new Event('location_selected'));
      onClose();
      if (onSkipClick && typeof onSkipClick === 'function') {
        onSkipClick({
          address: addressLine1,
          locality,
          pincode: Number(pincode),
          lat: editingAddress?.lat || newAddress?.lat,
          long: editingAddress?.long || newAddress?.long,
        });
      }
    }
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <div className={s.root}>
        {/* Address Type */}

        <div className={s.formGroup}>
          <p className={s.label}>Save address as</p>

          <div className={s.radioGroup}>
            {ADDRESS_TYPES.map(({ key, Icon }) => (
              <div
                key={key}
                className={`${s.radioItem} ${
                  addressType === key ? s.selected : ''
                }`}
                onClick={() => setAddressType(key)}
              >
                <Icon className={s.addressIcon} />
                <span>{key}</span>

                {addressType === key && (
                  <span className={s.checkIcon}>
                    <CheckIcon />
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Address line */}
        <div className={s.formGroup}>
          <p className={s.label}>Complete Address</p>
          <input
            type="text"
            placeholder="House no, building, area"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
          />
        </div>

        {/* Locality */}
        <div className={s.formGroup}>
          <p className={s.label}>Locality / Area</p>
          <input
            type="text"
            placeholder="Locality, Area, City"
            value={locality}
            onChange={(e) => setLocality(e.target.value)}
          />
        </div>

        {/* Pincode */}
        <div className={s.formGroup}>
          <p className={s.label}>Pincode</p>
          <input
            type="number"
            placeholder="6 digit pincode"
            value={pincode}
            readOnly
          />
        </div>

        {/* Save Button */}
        <Button
          label={
            saving
              ? 'Saving...'
              : editingAddress
                ? 'Update Address'
                : 'Save Address'
          }
          fullWidth
          onClick={handleSave}
          disabled={!isValid || saving}
        />
        {/* Skip Button */}
        {!hideSkipButton && (
          <Button
            label="Skip for now"
            fullWidth
            disabled={saving}
            variant="link"
            onClick={saveTempAddress}
          />
        )}
      </div>
    </BottomSheet>
  );
};

export default AddNewAddress;
